import type { CSSProperties } from 'react';
import type { GameParticipantView, GameSessionView, ResourceKey } from '@creativitas/shared';
import { LANE_CAPACITY, RESOURCE_LABELS } from '@creativitas/shared';
import { initialsOf } from '@/components/domain/ParticipantChip';
import { RESOURCE_CHIP_ICONS } from '@/lib/assets';
import { BoardDice, type DiceRollState } from './BoardDice';
import { gameTurnPresentation } from './game-turn';
import classes from './GameBoard.module.css';

/**
 * Обзор закрытых секторов после итога демо (ТЗ демо, п. 8): клетки этих
 * секторов подсвечены на настоящем поле, выбор показывает их описание. Это
 * только просмотр — ни фишка, ни карты сюда не ходят.
 */
export type BoardSectorOverview = {
  sectors: { id: string; title: string; cellIndexes: number[] }[];
  selected: string | null;
  onSelect: (sectorId: string) => void;
};

export function GameBoard({
  session,
  dice,
  diceVisible = true,
  overview,
}: {
  session: GameSessionView;
  /**
   * Supplied by the workspace, which owns the throw so the button can start it.
   * Without it the board still shows the last result, just without the tumble.
   */
  dice?: DiceRollState;
  /** The workspace decides when the throw is still the turn's focus. */
  diceVisible?: boolean;
  overview?: BoardSectorOverview;
}) {
  if (!session.game.board) return null;
  const board = session.game.board;
  const players = session.participants.filter((participant) => participant.boardPosition !== null);
  const face = dice?.face ?? session.lastDice?.value ?? 1;
  const phase = dice?.phase ?? 'idle';
  const displayedTurnId = gameTurnPresentation(session).participantId;

  return (
    <div className={classes.viewport} data-testid="game-board-viewport" data-coach="board">
      <div className={classes.board} data-testid="game-board">
        <img className={classes.art} src={board.boardImage} alt="Игровое поле" />
        {board.cells.map((cell) => (
          <span
            key={cell.id}
            className={classes.cell}
            data-type={cell.cellType}
            /*
              A hit area over the printed circle, nothing more: the board art is
              the visual hero and already draws every cell and its icon (§7).
            */
            style={{
              left: `${cell.x}%`,
              top: `${cell.y}%`,
              width: `${cell.diameter ?? 4.3}%`,
              height: `${cell.diameter ?? 4.3}%`,
            }}
            title={String(cell.metadata?.label ?? `Клетка ${cell.index + 1}`)}
          />
        ))}
        {overview?.sectors.flatMap((sector) => sector.cellIndexes.map((index) => {
          const cell = board.cells[index];
          if (!cell) return null;
          return (
            <button
              key={`overview-${cell.id}`}
              type="button"
              className={classes.cellLocked}
              data-selected={overview.selected === sector.id}
              data-testid={`overview-cell-${cell.index}`}
              aria-label={`Сектор «${sector.title}» — закрыт в демо`}
              title={`${sector.title} — в полной игре`}
              onClick={() => overview.onSelect(sector.id)}
              style={{
                left: `${cell.x}%`,
                top: `${cell.y}%`,
                width: `${cell.diameter ?? 4.3}%`,
                height: `${cell.diameter ?? 4.3}%`,
              }}
            />
          );
        }))}
        {(board.lanes ?? []).map((lane) => {
          const owner = players.find((player) => player.resourceLane === lane.index);
          /* An unclaimed ray is left as printed — the art already draws it. */
          if (!owner) return null;
          const chips = laneChips(owner);
          return (
            <span key={`lane-${lane.index}`}>
              <span
                className={classes.laneClaim}
                style={{
                  left: `${lane.claim.x}%`,
                  top: `${lane.claim.y}%`,
                  width: `${lane.claim.diameter}%`,
                  height: `${lane.claim.diameter}%`,
                  background: owner.laneColor ?? 'var(--accent)',
                }}
                title={`Луч игрока: ${owner.displayName}`}
              />
              {/*
                The whole ray is ringed in the owner's colour, not just its tip.
                One dot at the end asks the reader to remember six colours and
                find the matching avatar; a coloured chain says whose ray it is
                wherever the eye lands on it.
              */}
              {lane.cells.map((cell, slot) => (
                <span
                  key={`lane-${lane.index}-slot-${slot}`}
                  className={classes.laneCell}
                  style={{
                    left: `${cell.x}%`,
                    top: `${cell.y}%`,
                    width: `${cell.diameter}%`,
                    height: `${cell.diameter}%`,
                    borderColor: owner.laneColor ?? 'var(--accent)',
                  }}
                  /* No tooltip: six empty slots repeating one name is noise —
                     the claim dot and the chips already answer «чей это луч». */
                />
              ))}
              {chips.map((resource, slot) => {
                const cell = lane.cells[slot];
                if (!cell) return null;
                /* Smaller than its cell, so the owner's ring stays visible under it. */
                const size = cell.diameter * CHIP_SCALE;
                return (
                  <img
                    key={`lane-${lane.index}-${slot}`}
                    className={classes.laneChip}
                    src={RESOURCE_CHIP_ICONS[resource]}
                    alt=""
                    style={{
                      left: `${cell.x}%`,
                      top: `${cell.y}%`,
                      width: `${size}%`,
                      height: `${size}%`,
                    }}
                    title={`${owner.displayName}: ${RESOURCE_LABELS[resource]}`}
                  />
                );
              })}
            </span>
          );
        })}

        {/* The sun is the board's centre of attention — the throw belongs there. */}
        <BoardDice face={face} phase={phase} visible={diceVisible} />
        {players.map((participant, index) => {
          const cell = board.cells[participant.boardPosition ?? 0];
          if (!cell) return null;
          const angle = (index / Math.max(players.length, 1)) * Math.PI * 2;
          const style = {
            left: `${cell.x}%`,
            top: `${cell.y}%`,
            background: participant.laneColor ?? 'var(--accent)',
            '--marker-x': `${Math.cos(angle) * 15}px`,
            '--marker-y': `${Math.sin(angle) * 15}px`,
          } as CSSProperties;
          return (
            <span
              key={participant.id}
              className={classes.marker}
              data-current={displayedTurnId === participant.id}
              data-testid={`player-marker-${participant.id}`}
              style={style}
              title={`${participant.displayName}: клетка ${(participant.boardPosition ?? 0) + 1}`}
            >
              {initialsOf(participant.displayName)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** A chip sits inside its cell, leaving the owner's ring showing around it. */
const CHIP_SCALE = 0.84;

/**
 * The chips lying on one player's ray, from the ring inwards (GAME-34).
 *
 * The order is the server's: it records each resource as it is earned. The demo
 * rebuilt the list here by grouping the counters, so a seventh resource
 * re-sorted chips that were already lying on the board. The counters stay in
 * the player panel — past six they keep growing and the ray does not.
 */
function laneChips(player: GameParticipantView): ResourceKey[] {
  return player.raySlots.slice(0, LANE_CAPACITY);
}
